import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  checkForNewBuild,
  hasSeenStaleBuild,
  isBuildCheckEnabled,
  markStaleBuildSeen,
  reloadOntoNewBuild,
} from '../utils/buildCheck';

/**
 * Router-level stale-build guard.
 *
 * On every in-app navigation it checks /version.json and, if a newer build has been deployed
 * since this tab loaded, reloads so the destination page runs the new bundle. The address has
 * already changed by then, so the reload lands on the page the user asked for.
 *
 * The reload is issued from the fetch callback rather than tied to this component staying
 * mounted: when the stale page crashes, React Router replaces the whole tree, this guard
 * included, with its error screen — and the reload must still happen.
 *
 * When the tab becomes visible again it checks in the background and only remembers the
 * result: reloading a page the user is in the middle of would discard their work, so the reload
 * waits for their next navigation, which then needs no round trip.
 *
 * Inert where no build id was injected (Jest) and on the Vite dev server, which has no
 * version.json. Must be rendered inside the router (it uses `useLocation`).
 */
export function VersionGuard(): null {
  const location = useLocation();

  useEffect(() => {
    if (!isBuildCheckEnabled()) {
      return;
    }
    if (hasSeenStaleBuild()) {
      reloadOntoNewBuild();
      return;
    }
    checkForNewBuild().then((stale) => {
      if (stale) {
        reloadOntoNewBuild();
      }
    });
  }, [location.pathname]);

  useEffect(() => {
    if (!isBuildCheckEnabled()) {
      return;
    }
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }
      checkForNewBuild().then((stale) => {
        if (stale) {
          markStaleBuildSeen();
        }
      });
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  return null;
}
